import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import {
  Beaker,
  Check,
  Clock,
  Code2,
  FileCode,
  FileText,
  Loader2,
  MoreHorizontal,
  Pause,
  Pencil,
  Play,
  Plus,
  Save,
  Trash2,
  X,
  XCircle,
} from "lucide-react";
import type { OnMount } from "@monaco-editor/react";

import { AppShell } from "@/components/AppShell";
import { LoginGate } from "@/components/LoginGate";
import { CodeEditor } from "@/components/CodeEditor";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StatusBanner } from "@/components/ui/status-banner";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/cn";
import { runTsExercise } from "@/lib/runtimes/ts";
import { runSqlExercise } from "@/lib/runtimes/sql";
import type { RunResult } from "@/lib/runtimes/types";
import { useActiveClientId } from "@/hooks/useActiveClient";
import { supabase } from "@/lib/supabase";
import type { Exercise, Language, SqlTest } from "@/lib/types";
import { NoClientPicked } from "@/components/NoClientPicked";

export const Route = createFileRoute("/admin")({
  component: AdminPage,
});

type BannerState =
  | { kind: "success" | "error" | "info"; title: string; detail?: string }
  | null;

function AdminPage() {
  return (
    <LoginGate title="DeepDive" subtitle="Gestioná los ejercicios.">
      {({ user, signOut }) => (
        <AppShell user={user} onLogout={signOut}>
          <AdminContent />
        </AppShell>
      )}
    </LoginGate>
  );
}

// =================== HELPERS ===================
function genId() {
  return "ex_" + Math.random().toString(36).slice(2, 10);
}

function emptyExercise(companyId: string | null): Exercise {
  return {
    id: genId(),
    title: "",
    language: "typescript",
    duration_min: 30,
    description: "",
    docs: "",
    starter_code: "",
    tests_code: "",
    reference_code: "",
    setup_sql: "",
    sql_tests: [],
    company_id: companyId,
    is_active: true,
  };
}

// =================== CONTENT ===================
type Mode = "idle" | "view" | "edit" | "create";

function AdminContent() {
  const qc = useQueryClient();
  const [activeClientId] = useActiveClientId();
  const [mode, setMode] = useState<Mode>("idle");
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [form, setForm] = useState<Exercise | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [banner, setBanner] = useState<BannerState>(null);
  const bannerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function showBanner(b: NonNullable<BannerState>, autoHide = 4000) {
    setBanner(b);
    if (bannerTimer.current) clearTimeout(bannerTimer.current);
    if (autoHide > 0) {
      bannerTimer.current = setTimeout(() => setBanner(null), autoHide);
    }
  }

  useEffect(() => () => {
    if (bannerTimer.current) clearTimeout(bannerTimer.current);
  }, []);

  const { data: exercises = [], isLoading } = useQuery({
    queryKey: ["exercises", { activeClientId }],
    enabled: !!activeClientId,
    queryFn: async (): Promise<Exercise[]> => {
      let q = supabase
        .from("exercises")
        .select("*")
        .order("created_at", { ascending: false });
      if (activeClientId) q = q.eq("company_id", activeClientId);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Exercise[];
    },
  });

  const upsertMutation = useMutation({
    mutationFn: async (ex: Exercise) => {
      const { error } = await supabase.from("exercises").upsert({
        id: ex.id,
        title: ex.title,
        language: ex.language,
        duration_min: ex.duration_min,
        description: ex.description,
        docs: ex.docs,
        starter_code: ex.starter_code,
        tests_code: ex.tests_code,
        reference_code: ex.reference_code,
        setup_sql: ex.setup_sql,
        sql_tests: ex.sql_tests,
        company_id: ex.company_id ?? activeClientId,
        is_active: ex.is_active,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["exercises"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("exercises").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["exercises"] }),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { error } = await supabase
        .from("exercises")
        .update({ is_active: isActive })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["exercises"] }),
  });

  function selectExercise(id: string) {
    const ex = exercises.find((e) => e.id === id);
    if (!ex) return;
    setCurrentId(id);
    setForm(ex);
    setMode("view");
  }

  function newExercise() {
    setCurrentId(null);
    setForm(emptyExercise(activeClientId));
    setMode("create");
  }

  function startEdit() {
    if (!form) return;
    setMode("edit");
  }

  function cancelEdit() {
    // Si estaba editando un existente, volvemos al view; si era nuevo, idle.
    if (mode === "edit" && currentId) {
      const original = exercises.find((e) => e.id === currentId);
      if (original) setForm(original);
      setMode("view");
    } else {
      setForm(null);
      setCurrentId(null);
      setMode("idle");
    }
  }

  async function togglePause() {
    if (!form || !currentId) return;
    const next = !form.is_active;
    try {
      await toggleActiveMutation.mutateAsync({ id: currentId, isActive: next });
      setForm({ ...form, is_active: next });
      showBanner({
        kind: "success",
        title: next ? "Ejercicio activado" : "Ejercicio pausado",
        detail: next
          ? "Ya aparece en el picker de invitaciones."
          : "No aparece más en el picker de invitaciones, pero queda visible en assignments viejos.",
      });
    } catch (e) {
      showBanner({
        kind: "error",
        title: "No se pudo actualizar",
        detail: e instanceof Error ? e.message : String(e),
      });
    }
  }

  async function save() {
    if (!form) return;
    if (!form.title.trim()) {
      showBanner({ kind: "error", title: "Pon un título" });
      return;
    }
    if (!form.starter_code.trim()) {
      showBanner({ kind: "error", title: "Starter code no puede estar vacío" });
      return;
    }
    if (form.language === "sql") {
      if (!form.setup_sql.trim()) {
        showBanner({
          kind: "error",
          title: "Para SQL necesitás setup SQL",
          detail: "Al menos un CREATE TABLE.",
        });
        return;
      }
      if (form.sql_tests.length === 0) {
        showBanner({ kind: "error", title: "Agregá al menos un test SQL" });
        return;
      }
      for (const t of form.sql_tests) {
        if (!Array.isArray(t.expectedRows)) {
          showBanner({
            kind: "error",
            title: `Test "${t.name}" inválido`,
            detail: "expectedRows debe ser un array JSON válido.",
          });
          return;
        }
      }
    } else {
      if (!form.tests_code.trim()) {
        showBanner({ kind: "error", title: "Tests no pueden estar vacíos" });
        return;
      }
      try {
        // Inyectamos stubs de los helpers para que `test(...)`, `expect(...)`
        // y `measureUnder(...)` no exploten en la fase de parse. Solo importa
        // que el array sea sintácticamente válido — la validación real corre
        // en el panel Validar.
        const arr = new Function(
          "test",
          "expect",
          "measureUnder",
          `return (${form.tests_code})`
        )(
          (name: string, fn: unknown) => ({ name, fn }),
          () =>
            new Proxy(
              {},
              { get: () => () => {} }   // toBe/toEqual/etc → no-op
            ),
          () => {}
        );
        if (!Array.isArray(arr) || arr.length === 0)
          throw new Error("debe ser un array no vacío");
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : "Sintaxis inválida";
        if (!confirm(`Tests no parsean: ${msg}\n¿Guardar igual?`)) return;
      }
    }
    try {
      await upsertMutation.mutateAsync(form);
      setCurrentId(form.id);
      setMode("view");
      showBanner({ kind: "success", title: "Ejercicio guardado" });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      showBanner({ kind: "error", title: "Error guardando", detail: msg }, 0);
    }
  }

  async function doDelete() {
    if (!currentId) return;
    try {
      await deleteMutation.mutateAsync(currentId);
      setForm(null);
      setCurrentId(null);
      setMode("idle");
      setDeleteOpen(false);
      showBanner({ kind: "success", title: "Ejercicio borrado" });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      showBanner({ kind: "error", title: "Error borrando", detail: msg }, 0);
    }
  }

  if (!activeClientId) {
    return (
      <NoClientPicked
        title="Elegí un cliente para ver sus ejercicios"
        description="Cada cliente tiene su propio banco de ejercicios. Elegí uno desde el dropdown de arriba a la izquierda."
      />
    );
  }

  return (
    <div className="flex h-[calc(100vh-3.5rem)]">
      {/* === Sidebar lista (columna izquierda interna) === */}
      <ExerciseList
        exercises={exercises}
        currentId={currentId}
        isLoading={isLoading}
        onSelect={selectExercise}
        onCreate={newExercise}
      />

      {/* === Panel principal === */}
      <section className="flex-1 min-w-0 flex flex-col overflow-hidden">
        {banner && (
          <div className="px-6 pt-5 shrink-0">
            <StatusBanner
              variant={banner.kind === "success" ? "success" : banner.kind === "error" ? "error" : "info"}
              title={banner.title}
              onDismiss={() => setBanner(null)}
            >
              {banner.detail}
            </StatusBanner>
          </div>
        )}

        {!form ? (
          <div className="px-6 py-5 overflow-y-auto">
            <EmptyEditor onCreate={newExercise} />
          </div>
        ) : mode === "view" ? (
          <div className="flex-1 min-h-0 p-6">
            <ExerciseView
              exercise={form}
              onEdit={startEdit}
              onTogglePause={togglePause}
              onDelete={() => setDeleteOpen(true)}
              togglePausePending={toggleActiveMutation.isPending}
            />
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
            <ExerciseEditor
              form={form}
              onChange={setForm}
              onSave={save}
              onCancel={cancelEdit}
              onDelete={() => setDeleteOpen(true)}
              isCreating={mode === "create"}
              saving={upsertMutation.isPending}
            />
          </div>
        )}
      </section>

      {/* Confirm delete */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Borrar ejercicio</DialogTitle>
            <DialogDescription>
              ¿Seguro? Esta acción no se puede deshacer. Si ya hay submissions
              referenciando este ejercicio, el borrado va a fallar.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={doDelete}
              loading={deleteMutation.isPending}
            >
              <Trash2 className="h-4 w-4" />
              Borrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =================== LISTA (sidebar interna) ===================
interface ListProps {
  exercises: Exercise[];
  currentId: string | null;
  isLoading: boolean;
  onSelect: (id: string) => void;
  onCreate: () => void;
}

function ExerciseList({
  exercises,
  currentId,
  isLoading,
  onSelect,
  onCreate,
}: ListProps) {
  return (
    <aside className="w-72 shrink-0 border-r border-border bg-background/40 flex flex-col">
      <div className="px-4 py-4 border-b border-border flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold tracking-tight">Ejercicios</h2>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {exercises.length}{" "}
            {exercises.length === 1 ? "ejercicio" : "ejercicios"}
          </p>
        </div>
        <Button size="sm" variant="primary" onClick={onCreate}>
          <Plus className="h-3.5 w-3.5" />
          Nuevo
        </Button>
      </div>

      <div className="flex-1 overflow-auto p-2">
        {isLoading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
        {!isLoading && exercises.length === 0 && (
          <div className="px-3 py-10 text-center text-xs text-muted-foreground">
            Sin ejercicios todavía.
          </div>
        )}
        <div className="space-y-0.5">
          {exercises.map((e) => (
            <button
              key={e.id}
              onClick={() => onSelect(e.id)}
              className={cn(
                "w-full text-left px-3 py-2.5 rounded-md border transition-colors",
                e.id === currentId
                  ? "border-primary/60 bg-primary/5"
                  : "border-transparent hover:bg-accent",
                !e.is_active && "opacity-60"
              )}
            >
              <div className="flex items-center gap-2 mb-1">
                <LangBadge language={e.language} />
                <span className="text-[10px] text-muted-foreground">
                  {e.duration_min} min
                </span>
                {!e.is_active && (
                  <Badge variant="muted" className="text-[9px]">
                    Pausado
                  </Badge>
                )}
              </div>
              <div className="font-medium text-sm truncate leading-tight">
                {e.title || "Sin título"}
              </div>
              <div className="text-[10px] text-muted-foreground font-mono mt-1 truncate">
                {e.id}
              </div>
            </button>
          ))}
        </div>
      </div>
    </aside>
  );
}

// =================== EXERCISE VIEW (read-only) ===================
interface ViewProps {
  exercise: Exercise;
  onEdit: () => void;
  onTogglePause: () => void;
  onDelete: () => void;
  togglePausePending: boolean;
}

function ExerciseView({
  exercise,
  onEdit,
  onTogglePause,
  onDelete,
  togglePausePending,
}: ViewProps) {
  const fileExt =
    exercise.language === "sql"
      ? "sql"
      : exercise.language === "typescript"
        ? "ts"
        : "js";

  return (
    <div className="flex flex-col h-full rounded-[var(--radius)] border border-border bg-card overflow-hidden">
      {/* Header fijo */}
      <div className="shrink-0 flex items-start justify-between gap-4 px-6 py-4 border-b border-border bg-card">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-semibold tracking-tight truncate">
              {exercise.title || "Sin título"}
            </h2>
            <LangBadge language={exercise.language} />
            {!exercise.is_active && (
              <Badge variant="warning" className="text-[10px]">
                <Pause className="h-2.5 w-2.5 mr-0.5" />
                Pausado
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-1.5">
            <span className="font-mono">{exercise.id}</span>
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {exercise.duration_min} min
            </span>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="iconSm"
              aria-label="Más acciones"
              disabled={togglePausePending}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[200px]">
            <DropdownMenuItem onClick={onEdit}>
              <Pencil className="h-3.5 w-3.5" />
              Editar
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onTogglePause}>
              {exercise.is_active ? (
                <>
                  <Pause className="h-3.5 w-3.5" />
                  Pausar
                </>
              ) : (
                <>
                  <Play className="h-3.5 w-3.5" />
                  Activar
                </>
              )}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onDelete}
              className="text-destructive focus:text-destructive focus:bg-destructive/10"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Borrar
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Body scrolleable */}
      <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-6">
        {/* Descripción */}
        <ViewSection title="Descripción">
          {exercise.description.trim() ? (
            <div
              className="text-sm leading-relaxed prose prose-invert prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: exercise.description }}
            />
          ) : (
            <EmptyHint text="Sin descripción cargada." />
          )}
        </ViewSection>

        {/* Docs */}
        {exercise.docs.trim() && (
          <ViewSection
            title="Docs"
            hint="Aparece en el panel lateral del test."
          >
            <pre className="text-xs font-mono bg-secondary/40 border border-border rounded-md p-3 overflow-x-auto whitespace-pre-wrap">
              {exercise.docs}
            </pre>
          </ViewSection>
        )}

        {/* Starter code */}
        <ViewSection title={`Starter code · solution.${fileExt}`}>
          <CodePreview language={exercise.language} value={exercise.starter_code} />
        </ViewSection>

        {/* Setup SQL + SQL tests, o tests JS */}
        {exercise.language === "sql" ? (
          <>
            <ViewSection title="Setup SQL">
              {exercise.setup_sql.trim() ? (
                <CodePreview language="sql" value={exercise.setup_sql} />
              ) : (
                <EmptyHint text="Sin setup SQL." />
              )}
            </ViewSection>
            <ViewSection
              title="Tests SQL"
              hint={`${exercise.sql_tests.length} ${exercise.sql_tests.length === 1 ? "test" : "tests"} definido${exercise.sql_tests.length === 1 ? "" : "s"}.`}
            >
              {exercise.sql_tests.length === 0 ? (
                <EmptyHint text="Sin tests SQL." />
              ) : (
                <ul className="space-y-2">
                  {exercise.sql_tests.map((t, i) => (
                    <li
                      key={i}
                      className="rounded-md border border-border bg-secondary/30 p-3"
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-xs font-medium">
                          {t.name || `Test ${i + 1}`}
                        </span>
                        {t.ignoreOrder && (
                          <Badge variant="outline" className="text-[9px]">
                            Ignora orden
                          </Badge>
                        )}
                      </div>
                      <pre className="text-[11px] font-mono text-muted-foreground overflow-x-auto">
                        {JSON.stringify(t.expectedRows, null, 2)}
                      </pre>
                    </li>
                  ))}
                </ul>
              )}
            </ViewSection>
          </>
        ) : (
          <ViewSection title="Tests">
            {exercise.tests_code.trim() ? (
              <CodePreview language="javascript" value={exercise.tests_code} />
            ) : (
              <EmptyHint text="Sin tests cargados." />
            )}
          </ViewSection>
        )}

        {/* Solución de referencia */}
        <ViewSection
          title="Solución de referencia"
          hint="Privada — solo se usa para validar los tests."
        >
          {exercise.reference_code.trim() ? (
            <CodePreview
              language={exercise.language}
              value={exercise.reference_code}
            />
          ) : (
            <EmptyHint text="Sin solución cargada." />
          )}
        </ViewSection>
      </div>
    </div>
  );
}

function ViewSection({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-baseline gap-2 mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </h3>
        {hint && <span className="text-[11px] text-muted-foreground/70">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function EmptyHint({ text }: { text: string }) {
  return (
    <p className="text-xs text-muted-foreground italic py-2 px-3 rounded-md border border-dashed border-border">
      {text}
    </p>
  );
}

function CodePreview({
  language,
  value,
}: {
  language: Language;
  value: string;
}) {
  // Calcular altura por cantidad de líneas para que se vea TODO el código
  // sin scroll interno (el scroll lo maneja el contenedor padre).
  // 20px lineHeight + 20px padding total. Wrap líneas largas para que las
  // que pasan el ancho cuenten doble.
  const lineCount = value.split("\n").length;
  const height = Math.max(80, lineCount * 20 + 20);
  return (
    <CodeEditor
      language={language}
      value={value}
      readOnly
      height={height}
    />
  );
}

// =================== EMPTY EDITOR ===================
function EmptyEditor({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="rounded-[var(--radius)] border border-border bg-card/50 text-center py-24 px-6">
      <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 border border-primary/30 mb-4">
        <FileCode className="h-5 w-5 text-primary" />
      </div>
      <h3 className="text-base font-semibold tracking-tight mb-1">
        Ningún ejercicio seleccionado
      </h3>
      <p className="text-sm text-muted-foreground mb-6">
        Elegí uno de la lista o creá uno nuevo para empezar.
      </p>
      <Button variant="primary" onClick={onCreate}>
        <Plus className="h-4 w-4" />
        Nuevo ejercicio
      </Button>
    </div>
  );
}

// =================== LANG BADGE ===================
function LangBadge({ language }: { language: Language }) {
  const map = {
    typescript: {
      label: "TS",
      className: "bg-info/15 text-info border-info/30",
    },
    javascript: {
      label: "JS",
      className: "bg-warning/15 text-warning border-warning/30",
    },
    sql: {
      label: "SQL",
      className: "bg-success/15 text-success border-success/30",
    },
  } as const;
  const c = map[language];
  return (
    <span
      className={cn(
        "inline-block px-1.5 py-0.5 text-[10px] font-semibold tracking-wider rounded border",
        c.className
      )}
    >
      {c.label}
    </span>
  );
}

// =================== EDITOR ===================
interface EditorProps {
  form: Exercise;
  onChange: (f: Exercise) => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete: () => void;
  isCreating: boolean;
  saving: boolean;
}

function ExerciseEditor({
  form,
  onChange,
  onSave,
  onCancel,
  onDelete,
  isCreating,
  saving,
}: EditorProps) {
  const upd = <K extends keyof Exercise>(k: K, v: Exercise[K]) =>
    onChange({ ...form, [k]: v });

  // Validación: corre los tests contra reference_code y muestra el panel.
  const monacoRef = useRef<{
    monaco: Parameters<OnMount>[1];
    modelUri: string;
  } | null>(null);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<RunResult | null>(null);

  async function validateTests() {
    if (validating) return;
    if (!form.reference_code.trim()) {
      setValidationResult({
        results: [],
        passedCount: 0,
        totalCount: 0,
        score: 0,
        fatalError: "Tenés que escribir una solución de referencia para validar.",
      });
      return;
    }
    if (form.language !== "sql" && !form.tests_code.trim()) {
      setValidationResult({
        results: [],
        passedCount: 0,
        totalCount: 0,
        score: 0,
        fatalError: "Tenés que escribir los tests para validar.",
      });
      return;
    }

    setValidating(true);
    try {
      if (form.language === "sql") {
        const r = await runSqlExercise({
          code: form.reference_code,
          exerciseSetupSql: form.setup_sql,
          exerciseSqlTests: form.sql_tests,
        });
        setValidationResult(r);
      } else {
        if (!monacoRef.current) {
          setValidationResult({
            results: [],
            passedCount: 0,
            totalCount: 0,
            score: 0,
            fatalError:
              "Editor TypeScript todavía cargando. Probá de nuevo en un segundo.",
          });
          return;
        }
        const r = await runTsExercise({
          code: form.reference_code,
          exerciseTestsCode: form.tests_code,
          monaco: monacoRef.current.monaco,
          modelUri: monacoRef.current.modelUri,
        });
        setValidationResult(r);
      }
    } finally {
      setValidating(false);
    }
  }

  function onReferenceMount(
    editor: Parameters<OnMount>[0],
    monaco: Parameters<OnMount>[1]
  ) {
    monacoRef.current = {
      monaco,
      modelUri: editor.getModel()?.uri.toString() ?? "",
    };
  }

  return (
    <div className="rounded-[var(--radius)] border border-border bg-card">
      {/* Editor header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <div className="flex items-center gap-3 min-w-0">
          <h2 className="text-base font-semibold tracking-tight truncate">
            {isCreating ? "Nuevo ejercicio" : "Editar ejercicio"}
          </h2>
          <Badge
            variant="outline"
            className="font-mono text-[10px] normal-case tracking-normal"
          >
            {form.id}
          </Badge>
        </div>
        <Button size="iconSm" variant="ghost" onClick={onCancel}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="p-6 space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="title">Título</Label>
            <Input
              id="title"
              placeholder="Sliding Window Rate Limiter"
              value={form.title}
              onChange={(e) => upd("title", e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="language">Lenguaje</Label>
            <Select
              value={form.language}
              onValueChange={(v) => upd("language", v as Language)}
            >
              <SelectTrigger id="language">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="typescript">TypeScript</SelectItem>
                <SelectItem value="javascript">JavaScript</SelectItem>
                <SelectItem value="sql">SQL (SQLite)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="duration">Duración (minutos)</Label>
          <Input
            id="duration"
            type="number"
            value={String(form.duration_min)}
            onChange={(e) =>
              upd("duration_min", parseInt(e.target.value, 10) || 30)
            }
            min={1}
            max={240}
            required
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="desc">Descripción (HTML permitido)</Label>
          <Textarea
            id="desc"
            placeholder="<p>Necesitamos limitar la cantidad de requests...</p>"
            value={form.description}
            onChange={(e) => upd("description", e.target.value)}
            rows={4}
            className="font-mono text-xs"
          />
          <p className="text-[11px] text-muted-foreground">
            Se muestra en el modal de instrucciones al candidato (al entrar y desde
            el botón "Instrucciones" del header).
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="docs">Docs (Markdown / HTML)</Label>
          <Textarea
            id="docs"
            placeholder="# APIs útiles&#10;&#10;- `performance.now()` — timestamp en ms..."
            value={form.docs}
            onChange={(e) => upd("docs", e.target.value)}
            rows={8}
            className="font-mono text-xs"
          />
          <p className="text-[11px] text-muted-foreground">
            Aparece en el panel lateral del test, accesible desde el botón "Docs".
            Útil para referencias de API, ejemplos, links, etc.
          </p>
        </div>

        <div className="space-y-2">
          <Label>Starter code</Label>
          <CodeEditor
            language={form.language}
            value={form.starter_code}
            onChange={(v) => upd("starter_code", v)}
            height={220}
          />
        </div>

        {form.language === "sql" ? (
          <>
            <div className="space-y-2">
              <Label>Setup SQL (esquema + datos)</Label>
              <CodeEditor
                language="sql"
                value={form.setup_sql}
                onChange={(v) => upd("setup_sql", v)}
                height={200}
              />
              <p className="text-[11px] text-muted-foreground">
                Corre antes de cada test. Ej:{" "}
                <code className="bg-secondary px-1 py-0.5 rounded text-foreground">
                  CREATE TABLE ...; INSERT INTO ...;
                </code>
              </p>
            </div>
            <SqlTestsEditor
              tests={form.sql_tests}
              onChange={(tests) => upd("sql_tests", tests)}
            />

            <div className="space-y-2">
              <Label>Solución de referencia SQL (privada — solo para validar)</Label>
              <CodeEditor
                language="sql"
                value={form.reference_code}
                onChange={(v) => upd("reference_code", v)}
                height={180}
              />
              <p className="text-[11px] text-muted-foreground">
                Tu query correcta del ejercicio. <strong>Validar</strong> la
                corre contra cada test SQL para confirmar que los expectedRows
                coinciden.
              </p>
            </div>

            <ValidationPanel
              validating={validating}
              result={validationResult}
              onValidate={validateTests}
              onDismiss={() => setValidationResult(null)}
            />
          </>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>
                Tests (array JS con helpers <code className="bg-secondary px-1 py-0.5 rounded text-foreground font-mono">test()</code>,{" "}
                <code className="bg-secondary px-1 py-0.5 rounded text-foreground font-mono">expect()</code>,{" "}
                <code className="bg-secondary px-1 py-0.5 rounded text-foreground font-mono">measureUnder()</code>)
              </Label>
              <CodeEditor
                language="javascript"
                value={form.tests_code}
                onChange={(v) => upd("tests_code", v)}
                height={300}
              />
              <details className="text-[11px] text-muted-foreground">
                <summary className="cursor-pointer hover:text-foreground inline-flex items-center gap-1.5">
                  <FileText className="h-3 w-3" />
                  Ver ejemplos de helpers
                </summary>
                <pre className="bg-secondary p-3 rounded-md mt-2 font-mono overflow-x-auto text-xs text-foreground/80">{`// El primer arg de cada test es la clase principal del candidato.
// Si tira error o devuelve false → falla. Si no tira / devuelve true → pasa.

[
  test("Constructor", RL => {
    new RL(3, 1000);
    // No tira → pasa
  }),

  test("Devuelve true al primer call", RL => {
    const x = new RL(3, 1000);
    expect(x.isAllowed("u1", 0)).toBe(true);
  }),

  test("Bloquea al 4to call", RL => {
    const x = new RL(3, 1000);
    x.isAllowed("u1", 0);
    x.isAllowed("u1", 0);
    x.isAllowed("u1", 0);
    expect(x.isAllowed("u1", 0)).toBe(false);
  }),

  test("Performance < 100ms", RL => {
    const x = new RL(10, 1000);
    measureUnder(100, () => {
      for (let i = 0; i < 2000; i++) x.isAllowed("c" + i, i);
    });
  })
]

// Helpers:
//   expect(v).toBe(x)          → ===
//   expect(v).toEqual(x)       → deep equal
//   expect(v).toBeTrue()       → v === true
//   expect(v).toBeFalse()      → v === false
//   expect(v).toBeGreaterThan(n)
//   expect(v).toBeLessThan(n)
//   expect(fn).toThrow()       → la fn tiene que tirar
//   measureUnder(ms, fn)       → tira si fn tarda más de ms`}</pre>
              </details>
            </div>

            <div className="space-y-2">
              <Label>Solución de referencia (privada — solo para validar los tests)</Label>
              <CodeEditor
                language={form.language}
                value={form.reference_code}
                onChange={(v) => upd("reference_code", v)}
                height={240}
                onMount={onReferenceMount}
              />
              <p className="text-[11px] text-muted-foreground">
                Tu solución correcta del ejercicio. El botón <strong>Validar</strong>{" "}
                la corre contra los tests para confirmar que todos pasan antes de
                invitar candidatos.
              </p>
            </div>

            <ValidationPanel
              validating={validating}
              result={validationResult}
              onValidate={validateTests}
              onDismiss={() => setValidationResult(null)}
            />
          </div>
        )}
      </div>

      <Separator />

      <div className="flex justify-between items-center px-6 py-4">
        {!isCreating ? (
          <Button variant="outlineDanger" size="sm" onClick={onDelete}>
            <Trash2 className="h-3.5 w-3.5" />
            Borrar
          </Button>
        ) : (
          <div />
        )}
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={onSave} loading={saving}>
            <Save className="h-4 w-4" />
            Guardar
          </Button>
        </div>
      </div>
    </div>
  );
}

// =================== SQL TESTS ===================
function SqlTestsEditor({
  tests,
  onChange,
}: {
  tests: SqlTest[];
  onChange: (tests: SqlTest[]) => void;
}) {
  function addTest() {
    onChange([...tests, { name: "", expectedRows: [], ignoreOrder: false }]);
  }
  function updateTest(i: number, patch: Partial<SqlTest>) {
    onChange(tests.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  }
  function removeTest(i: number) {
    onChange(tests.filter((_, idx) => idx !== i));
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Tests SQL</Label>
        <Button size="sm" variant="default" onClick={addTest}>
          <Plus className="h-3.5 w-3.5" />
          Agregar test
        </Button>
      </div>
      <div className="space-y-3">
        {tests.length === 0 && (
          <div className="text-sm text-muted-foreground italic py-3 px-4 border border-dashed border-border rounded-md text-center">
            Sin tests todavía.
          </div>
        )}
        {tests.map((t, i) => (
          <SqlTestRow
            key={i}
            test={t}
            onChange={(patch) => updateTest(i, patch)}
            onRemove={() => removeTest(i)}
          />
        ))}
      </div>
    </div>
  );
}

function SqlTestRow({
  test,
  onChange,
  onRemove,
}: {
  test: SqlTest;
  onChange: (patch: Partial<SqlTest>) => void;
  onRemove: () => void;
}) {
  const [raw, setRaw] = useState(JSON.stringify(test.expectedRows, null, 2));
  const [parseError, setParseError] = useState<string | null>(null);

  useEffect(() => {
    setRaw(JSON.stringify(test.expectedRows, null, 2));
  }, [test.expectedRows]);

  function commit() {
    try {
      const parsed = JSON.parse(raw || "[]");
      if (!Array.isArray(parsed)) throw new Error("Debe ser un array");
      setParseError(null);
      onChange({ expectedRows: parsed });
    } catch (e: unknown) {
      setParseError(e instanceof Error ? e.message : "JSON inválido");
    }
  }

  return (
    <div className="border border-border rounded-md p-3 space-y-2 bg-secondary/30">
      <div className="flex gap-2 items-center">
        <Input
          placeholder="Nombre del test"
          value={test.name}
          onChange={(e) => onChange({ name: e.target.value })}
          className="flex-1 h-8 text-xs"
        />
        <label className="flex items-center gap-1.5 cursor-pointer text-xs text-muted-foreground">
          <Checkbox
            checked={!!test.ignoreOrder}
            onCheckedChange={(v) => onChange({ ignoreOrder: !!v })}
          />
          Ignorar orden
        </label>
        <Button
          size="iconSm"
          variant="ghost"
          onClick={onRemove}
          className="text-destructive hover:text-destructive hover:bg-destructive/10"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
      <Textarea
        placeholder='[{"col": "val"}, ...]'
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        onBlur={commit}
        rows={3}
        className="font-mono text-xs"
      />
      {parseError && (
        <div className="text-destructive text-xs flex items-center gap-1.5">
          <Code2 className="h-3 w-3" />
          JSON inválido: {parseError}
        </div>
      )}
    </div>
  );
}

// =================== VALIDATION PANEL ===================
function ValidationPanel({
  validating,
  result,
  onValidate,
  onDismiss,
}: {
  validating: boolean;
  result: RunResult | null;
  onValidate: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className="rounded-[var(--radius)] border border-border bg-secondary/20 p-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-semibold tracking-tight">
            Validar tests contra solución de referencia
          </h3>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Corre tus tests contra tu propia solución. Todos deberían pasar.
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={onValidate}
          loading={validating}
        >
          <Beaker className="h-3.5 w-3.5" />
          Validar
        </Button>
      </div>

      {result && (
        <div className="mt-3 rounded-md border border-border bg-card p-3">
          <div className="flex items-center justify-between mb-2">
            {result.fatalError ? (
              <span className="text-xs text-destructive font-semibold">
                Error
              </span>
            ) : result.passedCount === result.totalCount ? (
              <span className="text-xs text-success font-semibold inline-flex items-center gap-1.5">
                <Check className="h-3 w-3" />
                {result.passedCount}/{result.totalCount} tests pasan
              </span>
            ) : (
              <span className="text-xs text-warning font-semibold inline-flex items-center gap-1.5">
                <XCircle className="h-3 w-3" />
                {result.passedCount}/{result.totalCount} tests pasan — revisar
                los que fallan
              </span>
            )}
            <button
              type="button"
              onClick={onDismiss}
              className="text-[11px] text-muted-foreground hover:text-foreground"
            >
              Cerrar
            </button>
          </div>

          {result.fatalError && (
            <div className="text-xs text-destructive font-mono whitespace-pre-wrap">
              {result.fatalError}
            </div>
          )}

          {result.results.length > 0 && (
            <ul className="space-y-1 text-xs">
              {result.results.map((r, i) => (
                <li key={i} className="flex items-start gap-2">
                  {r.passed ? (
                    <Check className="h-3 w-3 text-success mt-0.5 shrink-0" />
                  ) : (
                    <XCircle className="h-3 w-3 text-destructive mt-0.5 shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div
                      className={
                        r.passed ? "text-foreground/80" : "text-destructive"
                      }
                    >
                      {r.name}
                    </div>
                    {r.error && (
                      <div className="text-[10px] text-muted-foreground font-mono mt-0.5 break-all">
                        └─ {r.error}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
